/**
 * A rule that watches one flow's recent runs and emails when failures pass a threshold.
 *
 * A rule is a window and a floor as much as a threshold: it looks at the last `windowMinutes`,
 * stays quiet until `minSampleSize` runs have happened, and once it has fired says nothing for
 * `cooldownMinutes`.
 */
import { OneOrManySchema } from 'notable-common';
import { z } from 'zod';
import { FlowApiPageLimitSchema, FlowApiPageOffsetSchema } from '../pagination/pageLimit.js';
export var FlowAlertRuleThresholdType;
(function (FlowAlertRuleThresholdType) {
    FlowAlertRuleThresholdType["Count"] = "Count";
    FlowAlertRuleThresholdType["Percentage"] = "Percentage";
})(FlowAlertRuleThresholdType || (FlowAlertRuleThresholdType = {}));
export var FlowAlertRuleRunMode;
(function (FlowAlertRuleRunMode) {
    FlowAlertRuleRunMode["Enabled"] = "Enabled";
    FlowAlertRuleRunMode["Disabled"] = "Disabled";
})(FlowAlertRuleRunMode || (FlowAlertRuleRunMode = {}));
/** Whether a listing covers live rules, deleted ones, or both. */
export var FlowAlertRuleVisibility;
(function (FlowAlertRuleVisibility) {
    FlowAlertRuleVisibility["Active"] = "Active";
    FlowAlertRuleVisibility["ActiveAndDeleted"] = "ActiveAndDeleted";
    FlowAlertRuleVisibility["Deleted"] = "Deleted";
})(FlowAlertRuleVisibility || (FlowAlertRuleVisibility = {}));
/** The fields a rule is described by, shared between creating one and reading one back. */
export const FlowAlertRuleValuesSchema = z.object({
    organizationId: z.uuid().describe('Organization the rule belongs to.'),
    flowId: z.uuid().describe('Flow the rule watches.'),
    displayName: z.string().min(1).describe('Name the rule is listed under.'),
    thresholdType: z
        .enum(FlowAlertRuleThresholdType)
        .describe('Whether the threshold counts failures or takes their share.'),
    thresholdValue: z
        .number()
        .nonnegative()
        .describe('Failures, or percentage of them, at which the rule fires.'),
    windowMinutes: z
        .number()
        .int()
        .min(1)
        .max(60)
        .default(20)
        .describe('How far back the rule looks.'),
    minSampleSize: z
        .number()
        .int()
        .min(1)
        .default(50)
        .describe('How many runs the window needs before the rule may fire.'),
    cooldownMinutes: z
        .number()
        .int()
        .min(5)
        .default(60)
        .describe('How long the rule stays quiet after firing.'),
    runMode: z
        .enum(FlowAlertRuleRunMode)
        .default(FlowAlertRuleRunMode.Enabled)
        .describe('Whether the rule is watching.'),
});
/** Body `POST /flow-alert-rules` accepts. */
export const FlowAlertRuleCreateBodySchema = FlowAlertRuleValuesSchema.extend({
    recipients: z.array(z.email()).min(1).describe('Who is emailed when the rule fires.'),
});
/**
 * Body `PATCH /flow-alert-rules/{flowAlertRuleId}` accepts.
 *
 * Neither the organization nor the flow is among them: a rule that changed flow would be a
 * different rule, and its history of firings would belong to nothing.
 */
export const FlowAlertRuleUpdateBodySchema = FlowAlertRuleValuesSchema.omit({
    organizationId: true,
    flowId: true,
})
    .extend({
    // The stored value is overwritten by whatever this body carries, so a surviving default would
    // reset the field on every request that left it out.
    windowMinutes: z.number().int().min(1).max(60),
    minSampleSize: z.number().int().min(1),
    cooldownMinutes: z.number().int().min(5),
    runMode: z.enum(FlowAlertRuleRunMode),
})
    .partial()
    .extend({
    recipients: z.array(z.email()).min(1).optional().describe('Replaces the current recipients.'),
});
/** Path parameters the endpoints addressing one rule accept. */
export const FlowAlertRuleIdParamsSchema = z.object({
    flowAlertRuleId: z.uuid().describe('The rule.'),
});
/** Query parameters `GET /flow-alert-rules` accepts. */
export const FlowAlertRuleListQuerySchema = z.object({
    organizationId: OneOrManySchema(z.uuid())
        .describe('Return only rules of this organization, or of any of these organizations.')
        .optional(),
    flowId: z.uuid().describe('Return only rules watching this flow.').optional(),
    runMode: OneOrManySchema(z.enum(FlowAlertRuleRunMode))
        .describe('Return only rules in this mode.')
        .optional(),
    visibilityMode: z
        .enum(FlowAlertRuleVisibility)
        .optional()
        .default(FlowAlertRuleVisibility.Active)
        .describe('Whether to cover deleted rules.'),
    limit: FlowApiPageLimitSchema.default(100).describe('How many rules to return.'),
    offset: FlowApiPageOffsetSchema.default(0).describe('How many matches to skip, for paging.'),
});
//# sourceMappingURL=flowAlertRule.js.map